/**
 * SyproFlow Welcome Screen v3.0.0
 * =================================
 * Post-login onboarding: collects account type (company/individual),
 * org name, creates organization via /auth/register-org, then proceeds to app.
 *
 * Usage:
 *   As onboarding (auth mode):   <WelcomeScreen onComplete={() => navigate('/app')} />
 *   As empty state (legacy mode): <WelcomeScreen onUpload={() => fileInputRef.current?.click()} />
 */

import React, { useState, useEffect } from 'react';
import { Upload, FileSpreadsheet, Zap, ArrowRight, CheckCircle, Shield, Loader2, Building2, User } from 'lucide-react';

const API_BASE = (() => {
  if (typeof window !== 'undefined' && window.SYPROFLOW_CONFIG?.API_BASE_URL) {
    return window.SYPROFLOW_CONFIG.API_BASE_URL;
  }
  if (import.meta?.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('syproflow_api_base');
    if (stored) return stored;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.syproflow.com' || host === 'syproflow.com' || host === 'www.syproflow.com') {
      return 'https://api.syproflow.com';
    }
  }
  return 'http://localhost:8000';
})();

export default function WelcomeScreen({ onUpload, onComplete }) {
  const [visible, setVisible] = useState(false);
  const [orgType, setOrgType] = useState(''); // 'company' | 'individual'
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isOnboarding = !!onComplete;
  const userEmail = typeof localStorage !== 'undefined' ? localStorage.getItem('syproflow_email') : '';

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleSubmitProfile = async () => {
    if (!orgType || !name.trim()) return;
    setLoading(true);
    setError('');

    const token = localStorage.getItem('syproflow_token');
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    try {
      // Register organization (required)
      const orgRes = await fetch(`${API_BASE}/auth/register-org`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ org_type: orgType, name: name.trim() }),
      });

      if (!orgRes.ok) {
        const data = await orgRes.json().catch(() => ({}));
        setError(data.detail || 'Failed to create account. Please try again.');
        setLoading(false);
        return;
      }

      // Also update user profile (sets display name for /me)
      const profileName = orgType === 'individual' ? name.trim() : name.trim();
      await fetch(`${API_BASE}/auth/update-profile`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: profileName, company: orgType === 'company' ? name.trim() : '' }),
      }).catch(() => {}); // Non-blocking — org registration is the critical path
    } catch {
      setError('Network error. Please check your connection.');
      setLoading(false);
      return;
    }

    setLoading(false);
    onComplete();
  };

  const steps = [
    { icon: <Upload className="w-5 h-5" />, title: 'Upload', desc: 'Drop any PDF, image, or scan' },
    { icon: <Zap className="w-5 h-5" />, title: 'AI Extracts', desc: 'Our AI reads every line & number' },
    { icon: <FileSpreadsheet className="w-5 h-5" />, title: 'Export', desc: 'Download as Excel, CSV, or Tally' },
  ];

  return (
    <div
      className={isOnboarding ? "min-h-screen flex items-center justify-center bg-slate-50" : "flex-1 flex items-center justify-center h-full min-h-[300px] lg:min-h-[400px]"}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(12px)',
        transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)'
      }}
    >
      <div className="w-full max-w-xl px-6 py-8">

        {/* Hero */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
            style={{ background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa' }}
          >
            <span style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: '#22c55e', display: 'inline-block',
              animation: 'pulse-dot 2s infinite'
            }} />
            {isOnboarding ? 'Account created' : 'Beta — Free to use'}
          </div>

          <h2 className="text-2xl lg:text-3xl font-bold text-slate-800 mb-2" style={{ letterSpacing: '-0.02em' }}>
            Welcome to <span style={{ color: '#ea580c' }}>SyproFlow</span>
          </h2>
          <p className="text-sm text-slate-500 max-w-sm mx-auto leading-relaxed">
            {isOnboarding
              ? `Signed in as ${userEmail || 'you'}. Tell us a bit about yourself.`
              : 'Extract structured data from any document in seconds. Powered by AI.'
            }
          </p>
        </div>

        {/* Onboarding form */}
        {isOnboarding ? (
          <div className="max-w-sm mx-auto mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
              {/* Account type selector */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-slate-600 mb-2">Account type</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'company', label: 'Company', icon: <Building2 className="w-5 h-5" />, desc: 'Business or firm' },
                    { value: 'individual', label: 'Individual', icon: <User className="w-5 h-5" />, desc: 'Freelancer or self' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setOrgType(opt.value); setName(''); }}
                      className="flex flex-col items-center gap-1.5 p-3.5 rounded-xl border-2 transition-all duration-200"
                      style={{
                        borderColor: orgType === opt.value ? '#ea580c' : '#e2e8f0',
                        background: orgType === opt.value ? '#fff7ed' : '#fff',
                        color: orgType === opt.value ? '#ea580c' : '#64748b',
                      }}
                    >
                      {opt.icon}
                      <span className="text-sm font-semibold">{opt.label}</span>
                      <span className="text-[10px] opacity-70">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Contextual name field — shown after type is picked */}
              {orgType && (
                <div className="mb-5">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    {orgType === 'company' ? 'Company name' : 'Your name'}
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={orgType === 'company' ? 'Acme Pvt Ltd' : 'John Doe'}
                    autoFocus
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition"
                  />
                </div>
              )}

              {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

              <button
                onClick={handleSubmitProfile}
                disabled={loading || !orgType || !name.trim()}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)' }}
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Setting up...
                  </span>
                ) : 'Get Started'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 3 Steps */}
            <div className="flex items-center justify-center gap-3 mb-8">
              {steps.map((step, i) => (
                <React.Fragment key={i}>
                  <div
                    className="flex flex-col items-center text-center"
                    style={{
                      opacity: visible ? 1 : 0,
                      transform: visible ? 'translateY(0)' : 'translateY(8px)',
                      transition: `all 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${0.2 + i * 0.15}s`
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center mb-2"
                      style={{
                        background: i === 0 ? '#ea580c' : '#f1f5f9',
                        color: i === 0 ? '#fff' : '#64748b'
                      }}
                    >
                      {step.icon}
                    </div>
                    <p className="text-xs font-semibold text-slate-700">{step.title}</p>
                    <p className="text-[10px] text-slate-400 max-w-[100px]">{step.desc}</p>
                  </div>
                  {i < steps.length - 1 && (
                    <ArrowRight className="w-4 h-4 text-slate-300 mt-[-16px]" />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Upload CTA */}
            <div className="text-center mb-8">
              <button
                onClick={onUpload}
                className="group inline-flex items-center gap-2.5 px-7 py-3 rounded-xl text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
                  boxShadow: '0 4px 14px rgba(234, 88, 12, 0.3)'
                }}
              >
                <Upload className="w-4 h-4 transition-transform group-hover:-translate-y-0.5" />
                Upload Your Document
              </button>
              <p className="text-[10px] text-slate-400 mt-2">PDF, JPG, PNG — up to 50 pages</p>
            </div>

            {/* Supported Documents */}
            <div
              className="text-center"
              style={{
                opacity: visible ? 1 : 0,
                transition: 'opacity 0.5s ease 0.6s'
              }}
            >
              <p className="text-xs text-slate-400">
                Works with any document type
              </p>
            </div>
          </>
        )}

        {/* Privacy Disclaimer */}
        <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
          <Shield className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
          <span>Your documents are never stored and never used for AI training. Files are processed in real-time and deleted immediately.</span>
        </div>

        {/* Beta info */}
        {!isOnboarding && (
          <div className="mt-8 flex items-center justify-center gap-4 text-[10px] text-slate-400">
            <span className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-green-500" /> No signup required
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-green-500" /> 15 free extractions
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-green-500" /> Industrial accuracy
            </span>
          </div>
        )}

        <style>{`
          @keyframes pulse-dot {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
      </div>
    </div>
  );
}
